import { ref, push, set, update, get, runTransaction } from 'firebase/database';
import { database } from '../firebaseConfig';
import { PIPELINE_SOURCE, DEAL_SOURCE, pipelineCheckInsActive, dealCheckInsActive, isUnderContract, pipelineCheckInTask, dealCheckInTask, finishesCheckIn } from '../shared/checkIns';

// Rolling check-in tasks on the agent's own task list (users/{uid}/tasks). Tasks only: no email or text.
const BI_PATH = 'crm/buyerInterests';
const nowIso = () => new Date().toISOString();

const pushTask = async (agent, data, uid) => {
  const r = push(ref(database, `users/${agent}/tasks`));
  await set(r, {
    role: 'Agent', appliesTo: 'All', mode: 'task', status: 'pending', assignee: agent,
    createdAt: nowIso(), updatedAt: nowIso(), createdBy: uid || null, completedAt: null, notes: '', ...data,
  });
  return r.key;
};

// New pipeline buyer: first check-in, 48 hours after creation.
export const startPipelineCheckIns = async (id, record, agent, uid, n = 1) => {
  if (!id || !agent || !pipelineCheckInsActive(record)) return null;
  return pushTask(agent, pipelineCheckInTask(record, id, n), uid);
};

// Converted to a transaction (or otherwise ended): cancel the open check-in so it leaves the task list.
export const cancelOpenPipelineCheckIn = async (id) => {
  try {
    const rec = (await get(ref(database, `${BI_PATH}/${id}`))).val();
    const agent = rec && (rec.alertTo || rec.assignedAgentId || rec.ownerUid);
    if (!rec?.checkInTaskId || !agent) return;
    const tPath = `users/${agent}/tasks/${rec.checkInTaskId}`;
    const t = (await get(ref(database, tPath))).val();
    if (t && t.status === 'pending') await update(ref(database, tPath), { status: 'cancelled', nextCheckInTaskId: 'stopped', notes: 'Check-ins ended', updatedAt: nowIso() });
  } catch (e) { console.error('Could not cancel the open check-in', e); }
};

// Deal went Under Contract / Pending: first weekly check-in for the deal's agent (once per deal).
export const startDealCheckIns = async (txId, ownerUid, uid) => {
  if (!txId || !ownerUid) return null;
  try {
    const path = `users/${ownerUid}/transactions/${txId}`;
    const tx = (await get(ref(database, path))).val();
    if (!tx || tx.checkInTaskId || !isUnderContract(tx) || !dealCheckInsActive(tx)) return null;
    const taskId = await pushTask(ownerUid, dealCheckInTask(tx, txId, ownerUid, 1), uid);
    await update(ref(database, path), { checkInTaskId: taskId });
    return taskId;
  } catch (e) { console.error('Could not start weekly check-ins', e); return null; }
};

// Called after a personal task changes status. When a check-in is marked done or N/A, the next one is
// created (if the record / deal is still active). Claims the task first so a double click never makes two.
export const advanceCheckIn = async (userId, taskId, newStatus, uid) => {
  try {
    const taskPath = `users/${userId}/tasks/${taskId}`;
    const task = (await get(ref(database, taskPath))).val();
    if (!finishesCheckIn(task, newStatus)) return null;
    const claim = await runTransaction(ref(database, `${taskPath}/nextCheckInTaskId`), (cur) => (cur ? undefined : 'pending'));
    if (!claim.committed) return null;
    const n = (Number(task.checkInNumber) || 1) + 1;
    let next = null;
    if (task.source === PIPELINE_SOURCE && task.buyerInterestId) {
      const rec = (await get(ref(database, `${BI_PATH}/${task.buyerInterestId}`))).val();
      if (pipelineCheckInsActive(rec)) {
        next = await pushTask(userId, pipelineCheckInTask(rec, task.buyerInterestId, n), uid);
        await update(ref(database, `${BI_PATH}/${task.buyerInterestId}`), { checkInTaskId: next });
      }
    } else if (task.source === DEAL_SOURCE && task.dealId) {
      const owner = task.dealOwnerUid || userId;
      const txPath = `users/${owner}/transactions/${task.dealId}`;
      const tx = (await get(ref(database, txPath))).val();
      if (dealCheckInsActive(tx)) {
        next = await pushTask(userId, dealCheckInTask(tx, task.dealId, owner, n), uid);
        await update(ref(database, txPath), { checkInTaskId: next });
      }
    }
    await update(ref(database, taskPath), { nextCheckInTaskId: next || 'stopped' });
    return next;
  } catch (e) { console.error('Could not create the next check-in', e); return null; }
};

// "Stop check-ins" toggle on a pipeline record. Stopping also cancels the open check-in task; turning it
// back on starts a fresh one on the normal schedule.
export const setPipelineCheckInsStopped = async (rec, stopped, uid) => {
  const agent = rec.alertTo || rec.assignedAgentId || rec.ownerUid;
  const patch = { checkInsStopped: !!stopped, updatedAt: nowIso() };
  if (stopped && rec.checkInTaskId && agent) {
    try {
      const tPath = `users/${agent}/tasks/${rec.checkInTaskId}`;
      const t = (await get(ref(database, tPath))).val();
      if (t && t.status === 'pending') await update(ref(database, tPath), { status: 'cancelled', nextCheckInTaskId: 'stopped', notes: 'Check-ins stopped', updatedAt: nowIso() });
    } catch (e) { console.error('Could not cancel the open check-in', e); }
  }
  if (!stopped && agent) {
    const t = await startPipelineCheckIns(rec.id, { ...rec, checkInsStopped: false }, agent, uid, 2);
    if (t) patch.checkInTaskId = t;
  }
  await update(ref(database, `${BI_PATH}/${rec.id}`), patch);
  return patch;
};
